import * as h3 from "h3-js";

/** H3 indexes are 64-bit; values beyond Number.MAX_SAFE_INTEGER must use string or bigint. */
export type H3CellIdInput = string | bigint;

/**
 * Normalize API / UI input to a 15-digit lowercase H3 index string for h3-js.
 */
export function cellIdToH3Index(cell: H3CellIdInput): string {
  if (typeof cell === "bigint") {
    return cell.toString(16).padStart(15, "0");
  }
  const t = cell.trim();
  if (t.startsWith("0x") || t.startsWith("0X")) {
    return t.slice(2).toLowerCase().padStart(15, "0");
  }
  if (/^\d+$/.test(t)) {
    return BigInt(t).toString(16).padStart(15, "0");
  }
  if (/^[0-9a-f]{1,15}$/i.test(t)) {
    return t.toLowerCase().padStart(15, "0");
  }
  throw new RangeError(`Invalid H3 cell id: ${cell}`);
}

export function headingDegrees(
  from: google.maps.LatLngLiteral,
  to: google.maps.LatLngLiteral,
): number {
  const fromLat = (from.lat * Math.PI) / 180;
  const toLat = (to.lat * Math.PI) / 180;
  let deltaLng = ((to.lng - from.lng) * Math.PI) / 180;
  if (deltaLng > Math.PI) deltaLng -= 2 * Math.PI;
  if (deltaLng < -Math.PI) deltaLng += 2 * Math.PI;

  const y = Math.sin(deltaLng) * Math.cos(toLat);
  const x =
    Math.cos(fromLat) * Math.sin(toLat) -
    Math.sin(fromLat) * Math.cos(toLat) * Math.cos(deltaLng);

  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

export function interpolateLatLng(
  from: google.maps.LatLngLiteral,
  to: google.maps.LatLngLiteral,
  t: number,
): google.maps.LatLngLiteral {
  let dLng = to.lng - from.lng;
  if (dLng > 180) dLng -= 360;
  if (dLng < -180) dLng += 360;

  return {
    lat: from.lat + (to.lat - from.lat) * t,
    lng: from.lng + dLng * t,
  };
}

export function getCellCenter(
  h3CellId: string,
  cache?: Map<string, google.maps.LatLngLiteral>,
): google.maps.LatLngLiteral | null {
  try {
    const cachedCenter = cache?.get(h3CellId);
    if (cachedCenter) return cachedCenter;
    const h3Index = cellIdToH3Index(h3CellId);
    const [lat, lng] = h3.cellToLatLng(h3Index);
    const point = { lat, lng };
    cache?.set(h3CellId, point);
    return point;
  } catch {
    return null;
  }
}

export function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

export const H3_MAP_MIN_ZOOM = 6;
export const H3_MAP_MAX_ZOOM = 8;
export const H3_MAP_SINGLE_POINT_ZOOM = 8;
export const H3_MAP_DEFAULT_ZOOM = 6;
export const H3_LARGE_SPAN_DEGREES = 35;

export function getMapViewForPoints(
  points: google.maps.LatLngLiteral[],
): { center: google.maps.LatLngLiteral; zoom: number; isWideSpread: boolean } {
  if (!points.length) {
    return { center: { lat: 20, lng: 0 }, zoom: H3_MAP_DEFAULT_ZOOM, isWideSpread: false };
  }

  if (points.length === 1) {
    return { center: points[0], zoom: H3_MAP_SINGLE_POINT_ZOOM, isWideSpread: false };
  }

  const lats = points.map((point) => point.lat);
  const lngs = points.map((point) => point.lng);
  const latSpan = Math.max(...lats) - Math.min(...lats);
  const lngSpan = Math.max(...lngs) - Math.min(...lngs);
  const isWideSpread = latSpan > H3_LARGE_SPAN_DEGREES || lngSpan > H3_LARGE_SPAN_DEGREES;

  return {
    center: {
      lat: median(lats),
      lng: median(lngs),
    },
    zoom: H3_MAP_MIN_ZOOM,
    isWideSpread,
  };
}
